import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_DRIVER_CASH_LEDGER_ROUTER } from "../../../services/api-config";

// Transaction types
export enum LedgerTransactionType {
  THU_TIEN_KHACH = 1,
  NOP_TIEN = 2,
  CHI_KHAC = 3,
}

// Reference types
export enum LedgerReferenceType {
  ORDER = 1,
  WITHDRAWAL = 2,
  ADJUSTMENT = 3,
}

// Status codes
export enum LedgerStatus {
  PENDING = 1,
  APPROVED = 2,
  REJECTED = 3,
}

export interface DriverCashLedger {
  id: number;
  driver_id: number;
  company_id: number;
  transaction_type: LedgerTransactionType;
  reference_type: LedgerReferenceType;
  reference_id?: number;
  amount: number;
  balance_before: number;
  balance_after: number;
  note?: string;
  receipt_id?: number;
  created_by?: number;
  approved_by?: number;
  status: LedgerStatus;
  created_at: string;
  updated_at: string;
  /** Chi nhánh chủ đơn của khoản tiền (null = chưa xác định). */
  branch_id?: number | null;
  customer_name?: string;
  // Joined fields
  driver_name?: string;
  order_code?: string;
}

export interface DriverCashWithdrawal {
  id: number;
  driver_id: number;
  company_id: number;
  amount: number;
  withdrawal_date: string;
  status: LedgerStatus;
  approved_by?: number;
  approved_at?: string;
  receipt_id?: number;
  ledger_id?: number;
  note?: string;
  reject_reason?: string;
  transfer_image?: string;
  created_at: string;
  updated_at: string;
  // Joined fields
  driver_name?: string;
  approved_by_name?: string;
}

export interface DriverBalance {
  driver_id: number;
  driver_name: string;
  driver_phone?: string;
  current_balance: number;
  total_collected: number;
  total_deposited: number;
  transaction_count: number;
  last_transaction_at?: string;
}

export interface DriverCashLedgerListFilters {
  driver_id?: number;
  /** Chi nhánh chủ đơn của khoản tiền; 0 = "chưa xác định" (dòng chưa gắn chi nhánh). */
  branch_id?: number;
  transaction_type?: number;
  status?: number;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
}

export interface DriverCashWithdrawalListFilters {
  driver_id?: number;
  status?: number;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
}

export interface DriverCashLedgerListResponse {
  list: DriverCashLedger[];
  total_record?: number;
  limit?: number;
}

export interface DriverCashWithdrawalListResponse {
  list: DriverCashWithdrawal[];
  total_record?: number;
  limit?: number;
}

export interface AllBalancesResponse {
  list: DriverBalance[];
  total_balance: number;
  total_collected: number;
  total_deposited: number;
}

export interface CreateWithdrawalPayload {
  driver_id: number;
  amount: number;
  note?: string;
}

export interface ApproveWithdrawalPayload {
  approved_by: number;
}

export interface RejectWithdrawalPayload {
  rejected_by: number;
  reject_reason: string;
}

export interface DebtReminderSettings {
  enabled: boolean;
  frequency_minutes: number;
  min_debt_threshold: number;
  start_hour: number;
  end_hour: number;
}

const DriverCashLedgerService = {
  // Get all driver balances
  allBalances: () => {
    const { request, baseResponse, loading } = useFetchData.Get<AllBalancesResponse>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: () =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_ALL_BALANCES,
        }),
      response: baseResponse,
      loading: loading,
    };
  },

  // Get ledger list
  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<DriverCashLedgerListResponse>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (params: DriverCashLedgerListFilters = {}) =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_LIST,
          data: {
            driver_id: params.driver_id,
            branch_id: params.branch_id,
            transaction_type: params.transaction_type,
            status: params.status,
            from_date: params.from_date,
            to_date: params.to_date,
            page: params.page ?? 1,
            limit: params.limit ?? 20,
          },
        }),
      response: baseResponse,
      loading: loading,
    };
  },

  // Get driver ledger
  driverLedger: () => {
    const { request, baseResponse, loading } = useFetchData.Get<DriverCashLedgerListResponse>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (driverId: number, params: DriverCashLedgerListFilters = {}) =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_DRIVER_LEDGER(driverId),
          data: {
            transaction_type: params.transaction_type,
            status: params.status,
            from_date: params.from_date,
            to_date: params.to_date,
            page: params.page ?? 1,
            limit: params.limit ?? 20,
          },
        }),
      response: baseResponse,
      loading: loading,
    };
  },

  // Get driver balance
  driverBalance: () => {
    const { request, baseResponse, loading } = useFetchData.Get<DriverBalance>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (driverId: number) =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_DRIVER_BALANCE(driverId),
        }),
      response: baseResponse,
      loading: loading,
    };
  },

  // Get withdrawals list
  withdrawals: () => {
    const { request, baseResponse, loading } = useFetchData.Get<DriverCashWithdrawalListResponse>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (params: DriverCashWithdrawalListFilters = {}) =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_WITHDRAWALS,
          data: {
            driver_id: params.driver_id,
            status: params.status,
            from_date: params.from_date,
            to_date: params.to_date,
            page: params.page ?? 1,
            limit: params.limit ?? 20,
          },
        }),
      response: baseResponse,
      loading: loading,
    };
  },

  // Create withdrawal
  createWithdrawal: () => {
    const { request, baseResponse, loading } = useFetchData.Post<DriverCashWithdrawal>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (payload: CreateWithdrawalPayload) =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_CREATE_WITHDRAWAL,
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    };
  },

  // Approve withdrawal
  approveWithdrawal: () => {
    const { request, baseResponse, loading } = useFetchData.Post<DriverCashWithdrawal>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (id: number, payload: ApproveWithdrawalPayload) =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_APPROVE_WITHDRAWAL(id),
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    };
  },

  // Reject withdrawal
  rejectWithdrawal: () => {
    const { request, baseResponse, loading } = useFetchData.Post<DriverCashWithdrawal>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (id: number, payload: RejectWithdrawalPayload) =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_REJECT_WITHDRAWAL(id),
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    };
  },
  // Get debt reminder settings
  getDebtReminderSettings: () => {
    const { request, baseResponse, loading } = useFetchData.Get<DebtReminderSettings>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: () =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_DEBT_REMINDER_SETTINGS,
        }),
      response: baseResponse,
      loading: loading,
    };
  },

  // Update debt reminder settings
  updateDebtReminderSettings: () => {
    const { request, baseResponse, loading } = useFetchData.Post<DebtReminderSettings>({
      projectId: ApiConfig.PROJECT_ID.ACCOUNTING_SERVICE,
    });
    return {
      fetch: (payload: DebtReminderSettings) =>
        request({
          endPoint: API_DRIVER_CASH_LEDGER_ROUTER.API_DEBT_REMINDER_SETTINGS,
          data: payload,
        }),
      response: baseResponse,
      loading: loading,
    };
  },
};

export default DriverCashLedgerService;
